import { Music2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const slugify = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export function SectionHeading({ eyebrow, title, dark = false, testId }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="h-px w-10 bg-primary" />
        <span
          className={`text-xs font-semibold uppercase tracking-[0.2em] ${
            dark ? "text-primary" : "text-muted-foreground"
          }`}
        >
          {eyebrow}
        </span>
      </div>
      <h2
        className={`mt-4 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl ${dark ? "text-white" : ""}`}
        data-testid={testId}
      >
        {title}
      </h2>
    </div>
  );
}

function Cursos({ courses, onSelectCourse }) {
  return (
    <section id="cursos" className="bg-white py-20 lg:py-28" data-testid="cursos-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading eyebrow="Cursos e instrumentos" title="Escolha seu instrumento" testId="cursos-title" />
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Aulas individuais e semanais, com plano de ensino adaptado ao seu nível — do primeiro acorde à performance.
        </p>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="instruments-strip">
          {courses.map((c) => (
            <button
              key={`tag-${c.id || c.name}`}
              type="button"
              onClick={() => onSelectCourse(c)}
              className="border border-foreground px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors hover:border-primary hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`instrument-tag-${slugify(c.instrument || c.name)}`}
            >
              {c.instrument || c.name}
            </button>
          ))}
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="cursos-grid">
          {courses.map((c) => (
            <article
              key={c.id || c.name}
              className="flex flex-col border border-border bg-white p-6 transition-colors hover:border-primary hover:shadow-[0_8px_30px_rgba(245,166,35,0.12)]"
              data-testid={`course-card-${slugify(c.name)}`}
            >
              <div className="flex h-11 w-11 items-center justify-center bg-primary text-primary-foreground">
                <Music2 size={20} />
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold">{c.name}</h3>
              <span className="mt-2 inline-flex w-fit border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                a partir de {c.min_age} anos
              </span>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-600">
                {c.description || `Aulas individuais de ${c.instrument} com acompanhamento personalizado.`}
              </p>
              {c.teachers?.length > 0 && (
                <p className="mt-3 text-xs text-muted-foreground">
                  <span className="font-semibold">Professores:</span> {c.teachers.join(", ")}
                </p>
              )}
              <Button
                variant="outline"
                className="mt-5 w-full border-foreground font-semibold hover:border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => onSelectCourse(c)}
                data-testid={`course-cta-${slugify(c.name)}`}
              >
                Quero aprender {c.instrument}
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Valores({ modalities, settings }) {
  return (
    <section id="valores" className="bg-[#0A0A0A] py-20 text-white lg:py-28" data-testid="valores-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading eyebrow="Modalidades e valores" title="Investimento simples e transparente" dark testId="valores-title" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-testid="modalities-grid">
          {modalities.map((m) => (
            <article
              key={m.id || m.name}
              className="border border-zinc-800 bg-zinc-950 p-8 transition-colors hover:border-primary"
              data-testid={`modality-card-${m.duration_minutes}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{m.name}</p>
              <p className="mt-4 font-heading text-5xl font-extrabold text-primary" data-testid={`modality-price-${m.duration_minutes}`}>
                {brl(m.monthly_price)}
              </p>
              <p className="mt-1 text-sm text-zinc-400">
                por mês · {m.duration_minutes} minutos por aula
              </p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-300">{m.description}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3" data-testid="valores-notes">
          {Number(settings?.enrollment_fee ?? 0) === 0 && (
            <span className="border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-300">
              Sem taxa de matrícula
            </span>
          )}
          <span className="border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-300">
            Mensalidade fixa — 4 ou 5 semanas, mesmo valor
          </span>
          {settings?.payment_methods && (
            <span className="border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-wider text-zinc-300">
              Pagamento: {settings.payment_methods.join(" · ")}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function Professores({ teachers }) {
  return (
    <section id="professores" className="bg-white py-20 lg:py-28" data-testid="professores-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading eyebrow="Professores" title="Quem vai te ensinar" testId="professores-title" />
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Músicos experientes e dedicados, cada um especialista no seu instrumento.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="teachers-grid">
          {teachers.map((t) => (
            <article
              key={t.name}
              className="border border-border bg-white p-6 transition-colors hover:border-primary"
              data-testid={`teacher-card-${slugify(t.name)}`}
            >
              {t.photo_url ? (
                <img src={t.photo_url} alt={`Foto de ${t.name}`} className="h-24 w-24 border border-border object-cover" />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center border border-border bg-[#0A0A0A] font-heading text-2xl font-extrabold text-primary">
                  {initials(t.name)}
                </div>
              )}
              <h3 className="mt-4 font-heading text-lg font-bold">{t.name}</h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {t.courses.map((c) => (
                  <span key={c} className="bg-secondary px-2 py-0.5 text-xs font-medium">
                    {c}
                  </span>
                ))}
              </div>
              {t.bio && <p className="mt-3 text-sm leading-relaxed text-zinc-600">{t.bio}</p>}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PublicCatalog({ courses, modalities, teachers, settings, onSelectCourse }) {
  return (
    <>
      <Cursos courses={courses} onSelectCourse={onSelectCourse} />
      <Valores modalities={modalities} settings={settings} />
      <Professores teachers={teachers} />
    </>
  );
}
